// Pure helpers for the Funnels Onboarding tab (Precision Funnel C12c). Every figure is the funnel
// stats API's (I-4): these functions order, label and word things; they never compute a rate.

import { formatCount, windowStart } from "./format";
import { DOORS, type Door, type FunnelOnboardingStats, type OnboardingStep, type OnboardingTotals } from "./stats-api";

/** The AXIS app launch day: the default start of every range. */
export const LAUNCH_DAY = "2026-10-27"; // PROD customer launch (bug 4070402f)
export const FUNNEL_SLUG = "precision-dosing";
export const RANGES = ["launch", "7", "30", "custom"] as const;
export type Range = (typeof RANGES)[number];
export const RANGE_LABELS: Record<Range, string> = { launch: "Since launch", "7": "7 days", "30": "30 days", custom: "Custom" };

export const GOALS = [
  "Lose fat",
  "Treat my health problem",
  "Improve my health",
  "Increase my energy levels",
  "Perform better",
  "Gain lean muscle",
] as const;
export const HEALTH_PROBLEM_GOAL = "Treat my health problem";
export const CUSTOM_RANGE_ERROR = "Start date must be before the end date.";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function parseDoor(value: unknown): Door {
  return DOORS.some((d) => d.value === value) ? (value as Door) : "all";
}

export function parseRange(value: unknown): Range {
  return (RANGES as readonly string[]).includes(String(value)) ? (value as Range) : "launch";
}

/** A non-empty picker value, else "all" (what the service reads as no filter). */
export function parseChoice(value: unknown): string {
  return typeof value === "string" && value !== "" ? value : "all";
}

/**
 * A picker's options with the URL's own value kept in front when the window's choices do not offer
 * it: the service lists choices from sign-ups inside the selected dates, so a filter a reader set is
 * never dropped because the range has no sign-ups. It shows as an active pill over zero people.
 */
export function withCurrent(options: readonly string[], value: unknown): string[] {
  const choice = parseChoice(value);
  return choice === "all" || options.includes(choice) ? [...options] : [choice, ...options];
}

/** The goal picker: the app database's own goal strings once C4 is live, else the Phase 1 list. */
export function goalOptions(choices: { goals: string[] }): string[] {
  return choices.goals.length > 0 ? choices.goals : [...GOALS];
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * The date window a range names (UTC days, like every window in this module). `custom` reads the
 * two URL dates and is an error when they are missing or reversed, so the table keeps its last
 * good read (UX §7.3).
 */
export function rangeWindow(
  range: Range,
  today: Date,
  custom: { from?: unknown; to?: unknown } = {},
): { from: string; to: string } | { error: string } {
  const to = isoDay(today);
  // Before launch day the window would run backwards; it closes on launch day instead (an empty read).
  if (range === "launch") return { from: LAUNCH_DAY, to: to < LAUNCH_DAY ? LAUNCH_DAY : to };
  if (range === "7") return { from: windowStart(7, today), to };
  if (range === "30") return { from: windowStart(30, today), to };
  const from = typeof custom.from === "string" && ISO_DAY.test(custom.from) ? custom.from : null;
  const until = typeof custom.to === "string" && ISO_DAY.test(custom.to) ? custom.to : to;
  if (!from) return { error: "Pick a start date." };
  if (from > until) return { error: CUSTOM_RANGE_ERROR };
  return { from, to: until };
}

export function doorLabel(door: Door): string {
  return DOORS.find((d) => d.value === door)?.label ?? "All doors";
}

const ASKED_LABELS: Record<string, string> = {
  fat_loss_only: "Fat loss only",
  fat_loss_cognition: "Fat loss + cognition goals",
  if_yes: "If yes",
};

/** The muted tag beside a route: who sees it, or when it is asked. Empty when every door sees it every time. */
export function stepTags(step: Pick<OnboardingStep, "door_tag" | "asked_tag">): string[] {
  const tags: string[] = [];
  if (step.door_tag) tags.push(step.door_tag);
  if (step.asked_tag) tags.push(ASKED_LABELS[step.asked_tag] ?? step.asked_tag);
  return tags;
}

/**
 * The steps a goal choice leaves visible (UX §7.4): with a goal picked, the Which kind group keeps
 * only that goal's screen and goal-only rows (How active) hide for other goals. `all` hides nothing.
 */
export function visibleSteps(steps: OnboardingStep[], goal: string): OnboardingStep[] {
  if (goal === "all") return steps;
  return steps.filter((s) => s.goal_key === null || s.goal_key === goal);
}

/** Top-level rows: every ungrouped step and every group row, in service order. */
export function topLevel(steps: OnboardingStep[]): OnboardingStep[] {
  return steps.filter((s) => s.is_group || s.group_key === null);
}

export function membersOf(steps: OnboardingStep[], groupKey: string): OnboardingStep[] {
  return steps.filter((s) => !s.is_group && s.group_key === groupKey);
}

export type TableRow = OnboardingStep & { index: number | null; nested: boolean; memberCount: number };

/**
 * The flat row list the table draws: top-level rows numbered by flow position (the number stays with
 * its step when the reader sorts by drop %), each open group followed by its screens. Order only.
 */
export function tableRows(steps: OnboardingStep[], open: ReadonlySet<string>, sortByDrop: "asc" | "desc" | null): TableRow[] {
  const top = topLevel(steps).map((step, i) => ({ step, index: i + 1 }));
  const ordered = sortByDrop
    ? [...top].sort((a, b) => {
        // A row with no drop % sits last whichever way the reader sorts.
        if (a.step.drop_rate == null || b.step.drop_rate == null) return (a.step.drop_rate == null ? 1 : 0) - (b.step.drop_rate == null ? 1 : 0);
        const diff = b.step.drop_rate - a.step.drop_rate;
        return sortByDrop === "desc" ? diff : -diff;
      })
    : top;
  const rows: TableRow[] = [];
  for (const { step, index } of ordered) {
    const members = step.is_group && step.group_key ? membersOf(steps, step.group_key) : [];
    rows.push({ ...step, index, nested: false, memberCount: members.length });
    if (step.is_group && step.group_key && open.has(step.group_key)) {
      for (const m of members) rows.push({ ...m, index: null, nested: true, memberCount: 0 });
    }
  }
  return rows;
}

export function formatSeconds(value: number | null | undefined): string {
  if (value == null) return "–";
  const s = Math.round(value);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${String(s % 60).padStart(2, "0")} s`;
}

export function formatDropRate(value: number | null | undefined): string {
  return value == null ? "–" : `${(value * 100).toFixed(1)}%`;
}

/** The biggest-drop floor the service applies (UX §7.4): bars under it draw grey. */
export const BIGGEST_DROP_FLOOR = 30;

/** Bar width as a share of the biggest eligible drop, so the worst step fills its cell. */
export function barWidth(step: Pick<OnboardingStep, "drop_rate">, steps: OnboardingStep[]): number {
  if (step.drop_rate == null) return 0;
  const eligible = topLevel(steps).filter((s) => (s.viewed ?? 0) >= BIGGEST_DROP_FLOOR && s.drop_rate != null);
  const max = Math.max(0, ...eligible.map((s) => s.drop_rate as number));
  if (max === 0) return 0;
  return Math.min(100, Math.round((step.drop_rate / max) * 100));
}

export function isBiggestDrop(step: Pick<OnboardingStep, "route">, totals: OnboardingTotals): boolean {
  return totals.biggest_drop !== null && totals.biggest_drop.route === step.route;
}

function formatDayShort(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(d);
}

export function rangeTitle(range: Range, window: { from: string; to: string }): string {
  if (range === "launch") return `Since launch (${formatDayShort(window.from)})`;
  if (range === "custom") return `${formatDayShort(window.from)} to ${formatDayShort(window.to)}`;
  return `Last ${range} days`;
}

/** "Since launch (Sat 10 Oct): 412 people started onboarding · 201 finished (48.8%)" */
export function summaryLine(stats: FunnelOnboardingStats, range: Range): string {
  const t = stats.totals;
  const people = t.started === 1 ? "person" : "people";
  const finished = t.finished == null ? "–" : formatCount(t.finished);
  const rate = t.finish_rate == null ? "" : ` (${formatDropRate(t.finish_rate)})`;
  return `${rangeTitle(range, stats.window)}: ${formatCount(t.started)} ${people} started onboarding · ${finished} finished${rate}`;
}

/** "Biggest drop: Health consent. 65 of 366 people (17.8%) left here." */
export function biggestDropLine(totals: OnboardingTotals): string | null {
  const b = totals.biggest_drop;
  if (!b) return null;
  return `Biggest drop: ${b.label}. ${formatCount(b.lost)} of ${formatCount(b.viewed)} people (${formatDropRate(b.drop_rate)}) left here.`;
}

/** The sign-up footnote (system spec C12, SPEC-OB-1b). */
export function choicesLine(totals: OnboardingTotals, choicesAsOf: string | null): string {
  if (choicesAsOf === null && totals.signed_up_people === 0) {
    return "Product and choices arrive when the app database copy (C4) and user_id at sign-up are live.";
  }
  const known = `Product and choices are known for ${formatCount(totals.signed_up_people)} of ${formatCount(totals.started)} viewers`;
  const left = totals.not_signed_up_people;
  return left > 0 ? `${known}; ${formatCount(left)} left before sign-up and are counted under All only.` : `${known}.`;
}

export function noDoorLine(totals: OnboardingTotals): string | null {
  if (totals.no_door_people === 0) return null;
  return `${formatCount(totals.no_door_people)} ${totals.no_door_people === 1 ? "person" : "people"} with no door recorded ${totals.no_door_people === 1 ? "is" : "are"} counted in All doors only.`;
}


export function unknownRoutesLine(stats: Pick<FunnelOnboardingStats, "unknown_routes">): string | null {
  const routes = stats.unknown_routes;
  if (routes.length === 0) return null;
  const list = routes.map((r) => r.route).join(", ");
  return `${formatCount(routes.length)} ${routes.length === 1 ? "screen" : "screens"} seen that ${routes.length === 1 ? "is" : "are"} not in the step list: ${list}.`;
}

/** The empty-state copy: nobody since launch, or nobody under the chosen filters. */
export function emptyLine(stats: FunnelOnboardingStats): string | null {
  if (stats.totals.started > 0) return null;
  const filtered = stats.door !== "all" || stats.product !== "all" || stats.goal !== "all" || stats.sub_goal !== "all";
  if (!filtered) return "No one has opened onboarding since launch yet. Figures appear the morning after the first person does.";
  const door = stats.door === "all" ? "any door" : doorLabel(stats.door);
  const product = stats.product === "all" ? "any product" : stats.product;
  const goal = stats.goal === "all" ? "" : stats.sub_goal === "all" ? ` with the goal ${stats.goal}` : ` with the goal ${stats.goal} (${stats.sub_goal})`;
  return `No one came through ${door} on ${product}${goal} in this range.`;
}

/** The sub-goal picker's options: the chosen goal's own kinds from the service, nothing under All or the health-problem goal. */
export function kindOptions(goal: string, subGoals: Record<string, string[]>): { value: string; label: string }[] {
  if (goal === "all" || goal === HEALTH_PROBLEM_GOAL) return [];
  return (subGoals[goal] ?? []).map((value) => ({ value, label: value }));
}

export const ONBOARDING_CAPTION =
  "Viewed = people shown the screen. Completed = went on to any later onboarding screen. Screens asked only some of the time (tagged) show fewer people. That is not a drop.";
export const FLOOR_CAPTION = "Biggest drop only counts steps seen by 30 or more people; their bars are purple, smaller steps grey.";
export const NOT_NAMED_TITLE = "Some screens have no page name.";
export const NOT_NAMED_TEXT = "Some screen views in this range carry no page name, so they are not counted.";
